import {
  type CtaHeaderProps,
  type FooterV4Props,
  type HeaderV4Props,
} from '@jouzen/ecom-components/layout';
import { useDynamicConfig } from '@statsig/react-bindings';
import { usePathname } from 'next/navigation';

import type { EyebrowConfig } from '@/app/components/Eyebrow/types';
import useDiscountMessage from '@/app/components/PageLayout/_hooks/useDiscountMessage';
import useLanguageSwitcher from '@/app/hooks/useLanguageSwitcher';

import useEyebrowConfig from './useEyebrowConfig';
import useFooterProps from './useFooterProps';
import useHeaderProps from './useHeaderProps';
import usePageAnalytics from './usePageAnalytics';

interface PageLayoutProps {
  enableScrollAnimation: boolean;
  isCarouselInView: boolean;
  parentCtaHeaderProps?: CtaHeaderProps | undefined;
  parentFooterProps?: Partial<FooterV4Props> | undefined;
  parentHeaderProps?: Partial<HeaderV4Props> | undefined;
}

const usePageLayout = ({
  enableScrollAnimation = false,
  isCarouselInView = false,
  parentCtaHeaderProps,
  parentFooterProps,
  parentHeaderProps,
}: PageLayoutProps) => {
  const currentPath = usePathname();
  const { locale, onLocaleChange, onLanguageSwitcherClick } =
    useLanguageSwitcher();
  const { displayMessage } = useDiscountMessage({
    isAccessory: false,
  });
  const config = useDynamicConfig('eyebrow');
  const eyebrowConfig = config.value as EyebrowConfig;
  const activeEyebrowConfig = Object.keys(config.value).length > 0;

  const eyebrowMessageType = useEyebrowConfig({
    hasActiveStatsigConfig: activeEyebrowConfig,
    hasDiscountMessage: Boolean(displayMessage),
    statsigValue: eyebrowConfig,
  });

  const {
    onFooterLinkClick,
    onHamburgerClick,
    onMenuLinkClick,
    sendEmailSubmitAnalytics,
  } = usePageAnalytics();

  const headerProps = useHeaderProps({
    currentPath,
    enableScrollAnimation,
    isCarouselInView,
    locale,
    onHamburgerClick,
    onMenuLinkClick,
    parentHeaderProps,
  });

  const footerProps = useFooterProps({
    currentPath,
    locale,
    onFooterLinkClick,
    onLocaleChange,
    onLanguageSwitcherClick,
    parentFooterProps,
    sendEmailSubmitAnalytics,
  });

  // only pass ctaHeaderProps to layout if it is defined
  const ctaHeaderProps = parentCtaHeaderProps
    ? {
        ctaHeaderProps: {
          inverse: headerProps.inverse ?? false,
          ...parentCtaHeaderProps,
        },
      }
    : {};

  return {
    ctaHeaderProps,
    displayMessage,
    eyebrowConfig,
    eyebrowMessageType,
    footerProps,
    headerProps,
  };
};

export default usePageLayout;
